import React, { useState, useEffect } from 'react';

import AppBar from '@material-ui/core/AppBar';
import Radio from '@material-ui/core/Radio';
import Tab from '@material-ui/core/Tab';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tabs from '@material-ui/core/Tabs';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import Message from '../../../widgets/legacy-message';
import { ReactComponent as LoadingLogo } from '../../../widgets/loading-logo.svg';
import { HighlightedTableCell } from '../../../widgets/table-components/table-header-cell';

type Props = EmployeeDirectoryWizardStepProps;

const DEFAULT_DISPLAYED_ROWS = 3;

type WorkSheetHeaderSelectorProps = {
  rows: Array<Array<string>>;
  selectedHeaderRow: number;
  onHeaderRowChange: (headerRow: number) => void;
};
const WorkSheetHeaderSelector = ({
  rows,
  selectedHeaderRow,
  onHeaderRowChange,
}: WorkSheetHeaderSelectorProps) => {
  const [limitRows, setLimitRows] = useState<boolean>(true);

  const limitedRows = limitRows ? rows.slice(0, DEFAULT_DISPLAYED_ROWS) : rows;
  const maxColumns = limitedRows
    ? limitedRows.reduce((acc, curr) => {
        if (curr.length > acc) {
          return curr.length;
        }
        return acc;
      }, 1) + 1
    : 1;

  const handleClickViewMoreRows = () => setLimitRows(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsedNumber = Number.parseInt(event.target.value, 10);
    if (Number.isNaN(parsedNumber)) {
      return;
    }
    onHeaderRowChange(parsedNumber);
  };

  return (
    <div className="u-width-100 u-overflow-auto">
      <Table size="small">
        <TableBody>
          {limitedRows.map((row, rowIdx) => {
            const CellComponent = selectedHeaderRow === rowIdx ? HighlightedTableCell : TableCell;
            return (
              <TableRow>
                <CellComponent>
                  <Radio
                    checked={selectedHeaderRow === rowIdx}
                    onChange={handleChange}
                    value={rowIdx}
                  />
                </CellComponent>
                {row.map((col) => (
                  <CellComponent key={col}>{col}</CellComponent>
                ))}
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell colSpan={maxColumns}>
              {limitRows ? (
                <div className="typo-link" role="presentation" onClick={handleClickViewMoreRows}>
                  View all rows...
                </div>
              ) : null}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

type TruncationData = {
  wasTruncated: boolean;
  fullRangeRows?: number;
  truncatedRangeRows?: number;
};

export const EmployeeDirectoryImportHeaderConfirmation = ({
  employeeDirectoryImportId,
  onNext,
  onCancel,
  PageIndicator,
}: Props) => {
  const [singleWorksheet, setSingleWorksheet] = useState<Array<Array<string>> | null>(null);
  const [singleWorksheetTruncationData, setSingleWorksheetTruncationData] =
    useState<TruncationData | null>(null);
  const [multipleWorksheets, setMultipleWorksheets] = useState<{
    [worksheetName: string]: Array<Array<string>>;
  } | null>(null);
  const [multipleWorksheetsTruncationData, setMultipleWorksheetsTruncationData] = useState<{
    [worksheetName: string]: TruncationData;
  } | null>(null);
  const [worksheets, setWorksheets] = useState<Array<string>>([]);
  const [selectedWorksheet, setSelectedWorksheet] = useState<number>(0);
  const [selectedHeaderRow, setSelectedHeaderRow] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (!hasInitialized) {
      Api.get(`/employee-directory-import/${employeeDirectoryImportId}/parse-file`)
        .then((resp) => {
          resp.json().then((data: any) => {
            if (data.errorMessage || typeof data !== 'object') {
              setErrorMessage('Failed to load data');
            } else if (data.parsedWorksheet) {
              setSingleWorksheet(data.parsedWorksheet);
              setSingleWorksheetTruncationData(data.parsedWorksheetTruncationData);
            } else if (data.parsedWorksheets) {
              setMultipleWorksheets(data.parsedWorksheets);
              setWorksheets(Object.keys(data.parsedWorksheets));
              setMultipleWorksheetsTruncationData(data.parsedWorksheetsTruncationData);
            }
          });
        })
        .catch(() => {
          setErrorMessage('Failed to load data');
        });
      setHasInitialized(true);
    }
  }, [employeeDirectoryImportId, hasInitialized]);

  const handleOnNext = async () => {
    onNext({
      selectedHeaderRow,
      noHeaderRow: false,
      selectedWorksheet: worksheets[selectedWorksheet],
    });
  };

  const handleOnNextNoHeaderRow = async () => {
    onNext({
      noHeaderRow: true,
      selectedWorksheet: worksheets[selectedWorksheet],
    });
  };

  const handleHeaderRowChange = (newHeaderRow: number) => {
    setSelectedHeaderRow(newHeaderRow);
  };

  const handleWorksheetNameChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedWorksheet(newValue);
  };

  const rows =
    singleWorksheet ||
    (multipleWorksheets ? multipleWorksheets[worksheets[selectedWorksheet]] : null) ||
    null;

  let truncationMessage: string | null = null;
  let selectedTruncationData: TruncationData | null = null;
  if (singleWorksheetTruncationData && singleWorksheetTruncationData.wasTruncated) {
    selectedTruncationData = singleWorksheetTruncationData;
  } else if (
    multipleWorksheetsTruncationData &&
    multipleWorksheetsTruncationData[worksheets[selectedWorksheet]].wasTruncated
  ) {
    selectedTruncationData = multipleWorksheetsTruncationData[worksheets[selectedWorksheet]];
  }
  if (selectedTruncationData) {
    truncationMessage = `This file was truncated from ${selectedTruncationData.fullRangeRows?.toLocaleString()} rows to ${selectedTruncationData.truncatedRangeRows?.toLocaleString()} rows. It's possible that this was caused by blank rows, but if it's legitimate, please use multiple uploads to import the data.`;
  }

  return (
    <>
      <UiDialog.Content>
        <PageIndicator />
        <span className="typo-h5">Select header row</span>
        <p className="typo-subtitle">
          Below is the data we were able to parse from the file that was uploaded. Using the radio
          buttons, please select which row contains the headers that describe each column.
        </p>
        {errorMessage ? (
          <Message variant="error" className="u-margin-bottom">
            {errorMessage}
          </Message>
        ) : null}
        {truncationMessage ? (
          <Message variant="warning" className="u-margin-bottom" canBeDismissed={false}>
            <div className="u-flex-center">
              <MaterialDesignIcon
                iconName={ICON_NAMES.WARNING}
                color="error"
                className="u-margin-right-sm"
              />
              <div>{truncationMessage}</div>
            </div>
          </Message>
        ) : null}
        {!singleWorksheet && !multipleWorksheets && !errorMessage ? (
          <div className="u-textAlign-center">
            <LoadingLogo />
          </div>
        ) : null}
        {multipleWorksheets ? (
          <AppBar position="static">
            <Tabs value={selectedWorksheet} onChange={handleWorksheetNameChange}>
              {worksheets.map((ws) => (
                <Tab key={ws} label={ws} />
              ))}
            </Tabs>
          </AppBar>
        ) : null}
        {rows ? (
          <WorkSheetHeaderSelector
            rows={rows}
            selectedHeaderRow={selectedHeaderRow}
            onHeaderRowChange={handleHeaderRowChange}
          />
        ) : null}
        <div className="u-padding-top typo-subtitle">
          Please let the customer know if you think this file is unusable. Common reasons this can
          occur is if there is missing data (in particular, first or last name), the data not being
          in rows or columns, or if we were unable to parse the file.
        </div>
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={onCancel}>
          Cancel
        </UiButton>
        <div>
          <UiButton variant="primary" className="u-margin-left" onClick={handleOnNextNoHeaderRow}>
            There is no header row
          </UiButton>
          <UiButton
            variant="primary"
            className="u-margin-left"
            disabled={Boolean(errorMessage)}
            onClick={handleOnNext}
          >
            Next
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
};
